import dayjs from "dayjs"; // Importing dayjs for date manipulation
import { observer } from "mobx-react"; // Importing observer from mobx-react for state management
import React, { useEffect } from "react"; // Importing React and useEffect hook
import { Modal } from "react-bootstrap"; // Importing Modal from react-bootstrap
import toast from "react-hot-toast"; // Importing toast for notifications
import { FormattedMessage } from "react-intl"; // Importing FormattedMessage for internationalization
import { useHistory } from "react-router-dom"; // Importing useHistory for navigation
import {
  calculateTreatmentTimings,
  DATE_FORMAT,
  getOptionNameFromServiceName,
} from "../../../../constants/constants"; // Importing utility function for treatment timings
import bookingStatusEnum from "../../../../constants/enums/booking-status-enum"; // Importing booking status enum
import { BookingTypeEnum } from "../../../../constants/enums/booking-type-enum";
import { serviceTypeEnum } from "../../../../constants/enums/service-type-enum";
import { useStore } from "../../../../contexts/store-provider"; // Importing custom store provider
import FormLoader from "../../../../shared-components/FormLoader/FormLoader"; // Importing FormLoader component
import ShowPopupHOC from "../../../../shared-components/popup/show/show-popup-hoc";
import RoutesConstants from "../../../../shared-components/routes/routes-constants"; // Importing route constants
import { formatMessage } from "../../../../translations/format-message"; // Importing formatMessage for translations
import AddEditCustomerManagement from "../../../customer-management/components/add-edit-customer-management";
import ReservationMemoForm from "../form-component/reservation-memo-form-component"; // Importing ReservationMemoForm component
import CancelReservationPopupHOC from "../hoc/cancel-reservation-popup-hoc"; // Importing HOC for cancel reservation popup
import RebookReservationPopupHOC from "../hoc/rebook-reservation-popup-hoc";
import { getUserTypeFromLocalStorage } from "../../../../helpers/localstorage-helper";
import userTypeEnum from "../../../../constants/enums/user-type-enum";

interface IProps {
  onModalClosed: () => void; // Callback for modal close
  cancelReservationToggleHandler: () => void; // Callback for toggling cancel reservation
  event: any; // Event data
  storeId: number;
  viewOnly: boolean; // Flag for view-only mode
  showShowPopup: boolean; // Flag to show add popup
  showPopupToggleHandler: () => void; // Callback for add toggle
}

const ReservationDetailsComponent = (props: IProps) => {
  const history = useHistory(); // Hook for navigation
  const {
    bookingManagementStore,
    reservationSchedulerStore,
    masterManagementStore,
    smrStore,
  } = useStore(); // Accessing stores
  const {
    GetAppointmentByIdService, // Service to get appointment by ID
    apponitmentDetail, // Appointment details
    appointmentByIdState, // State of appointment by ID
    resetAppointmentState, // Reset appointment state
    resetAppointmentDetail, // Reset appointment details
  } = bookingManagementStore;
  const {
    setSelectedAppointmentIdForPayment, // Sets the selected appointment ID for payment from SMR
    setSelectedSMRCustomerId,
    selectedSMRCustomerId, // Selected customer ID in SMR store
  } = smrStore;

  const {
    updateReservationMemoState, // State of reservation memo update
    UpdateReservationMemoService, // Service to update reservation memo
    resetUpdateReservationMemoState, // Reset reservation memo state
    UpdateReservationCancelStatusService, // Service to update reservation cancel status
    resetUpdateReservationCancelState, // Reset reservation cancel state
    updateReservationCancelState, // State of reservation cancel update
    UpdateBookingTypeService, // Service to update booking type
    resetBookingTypeState, // Reset booking type state
    updateBookingTypeState, // State of booking type update
  } = reservationSchedulerStore;

  const { getReservationSettingDetail } = masterManagementStore;

  const initialValues = apponitmentDetail; // Initial values for the form

  const openCustomerDetail = (Id: number) => {
    // onModalClosed(); // Close modal
    setSelectedSMRCustomerId(Id); // Set selected customer ID in SMR store
    props.showPopupToggleHandler(); // Trigger add toggle handler
    // history.push({
    //   pathname: RoutesConstants.CustomerManagement, // Navigate to customer management
    //   state: { customerId: Id }, // Pass customer ID
    // });
  };

  useEffect(() => {
    if (props.event?.Id > 0) {
      GetAppointmentByIdService(props.event?.Id); // Fetch appointment by ID
      // props.getAllEvents(props.event?.Id);
    }
  }, []);

  useEffect(() => {
    if (apponitmentDetail?.id > 0) {
      if(getUserTypeFromLocalStorage() != userTypeEnum[5]){
        if (
          apponitmentDetail?.BookingType == BookingTypeEnum.NewOnCall ||
          apponitmentDetail?.BookingType == BookingTypeEnum.NewVisit ||
          apponitmentDetail?.BookingType == BookingTypeEnum.NewWeb
        ) {
          UpdateBookingTypeService({
            BookingType:
              apponitmentDetail?.BookingType == BookingTypeEnum.NewOnCall
                ? BookingTypeEnum.OnCall
                : apponitmentDetail?.BookingType == BookingTypeEnum.NewVisit
                ? BookingTypeEnum.Visit
                : apponitmentDetail?.BookingType == BookingTypeEnum.NewWeb
                ? BookingTypeEnum.Web
                : apponitmentDetail?.BookingType, // Update booking type based on appointment status
            AppointmentId: apponitmentDetail?.id, // Appointment ID
          }); // Reset booking type state
        }
      }
    }
  }, [apponitmentDetail]);

  useEffect(() => {
    if (updateReservationMemoState.success) {
      toast.success(formatMessage("updated_success")); // Show success toast
      onModalClosed(); // Close modal
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateReservationMemoState.success]);

  useEffect(() => {
    if (updateReservationCancelState.success) {
      // toast.success(formatMessage("updated_success")); // Show success toast
      onCancelModalClosed(); // Close cancel modal
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateReservationCancelState.success]);

  useEffect(() => {
    if (updateReservationMemoState.error) {
      toast.error(formatMessage(updateReservationMemoState.error)); // Show error toast
      resetUpdateReservationMemoState(); // Reset memo state
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateReservationMemoState.error]);

  useEffect(() => {
    if (updateReservationCancelState.error) {
      toast.error(formatMessage(updateReservationCancelState.error)); // Show error toast
      resetUpdateReservationCancelState(); // Reset cancel state
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateReservationCancelState.error]);

  const onModalClosed = () => {
    resetAppointmentDetail(); // Reset appointment details
    resetAppointmentState(); // Reset appointment state
    props.onModalClosed(); // Trigger modal close callback
  };

  const treatments = calculateTreatmentTimings(
    apponitmentDetail.ServiceRequestedVMss, // List of requested services
    props.event?.StartTime, // Start time of the event
    apponitmentDetail?.PostBreakTime // Buffer time
  );

  const onSubmitForm = (values: any) => {
    let obj = {
      Id: apponitmentDetail?.id, // Appointment ID
      ReservationMemo: values?.ReservationMemo, // Reservation memo
      AppointmentStatus:
        values?.Reception == true
          ? bookingStatusEnum.InProcess // Update status if reception is true
          : apponitmentDetail?.AppointmentStatus, // Keep existing status
    };

    UpdateReservationMemoService(obj); // Update reservation memo
  };

  const onCancelModalClosed = () => {
    resetUpdateReservationCancelState(); // Reset cancel state
    props.cancelReservationToggleHandler(); // Trigger cancel toggle handler
    props.onModalClosed();
  };

  const cancelReservationSubmitHandler = (values: any) => {
    UpdateReservationCancelStatusService({
      Id: apponitmentDetail?.id, // Appointment ID
      CancellationType: Number(values?.CancellationType), // Cancellation type
    });
  };

  return (
    <React.Fragment>
      {props.showShowPopup && (
        <AddEditCustomerManagement
          id={selectedSMRCustomerId}
          viewOnly={false}
          routeFrom={RoutesConstants.SalesMedicalRecords}
          modalClosed={props.showPopupToggleHandler}
        />
      )}
      {appointmentByIdState.inProgress && (
        <FormLoader loading={appointmentByIdState.inProgress} /> // Show loader if in progress
      )}
      <Modal show={true} centered className="modal-md">
        {" "}
        {/* Modal component */}
        <Modal.Header>
          <Modal.Title>
            {<FormattedMessage id="reservation_details" />} {/* Modal title */}
            <button
              onClick={() => {
                onModalClosed(); // Close modal on button click
              }}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="Reservation-detail p-2">
            {" "}
            {/* Reservation details */}
            <div className="bg-light p-3 rounded">
              {" "}
              {/* Details container */}
              <div className="row">
                <div className="col-6">
                  <label className="text-muted bold">
                    {formatMessage("reservation_id")}
                  </label>{" "} 
                  {/* Reservation ID label */}
                </div>
                <div className="col-6">
                  <p>
                    {props.event?.Id} {/* Reservation ID */}
                  </p>
                </div>
              </div>
              {/* Details container */}
              <div className="row">
                <div className="col-6">
                  <label className="text-muted bold">
                    {formatMessage("customer_name")}
                  </label>{" "}
                  {/* Customer name label */}
                </div>
                <div className="col-6">
                  <p>
                    {props.event?.CustomerName} {/* Customer name */}
                    <a
                      onClick={() => {
                        openCustomerDetail(apponitmentDetail.CustomerId); // Open customer details
                      }}
                      className="iconsShare ms-2 btn btn-primary btn-sm"
                    >
                      <svg
                        className="me-1"
                        fill="currentColor"
                        height="16"
                        viewBox="0 0 24 24"
                        width="16"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g>
                          <path d="m11.9425 1.25h.1148c2.3084-.00001 4.1174-.00002 5.5289.18975 1.4447.19424 2.5848.59958 3.4796 1.49439s1.3001 2.03483 1.4944 3.47957c.1897 1.41148.1897 3.22052.1897 5.52889v.1148c0 2.3084 0 4.1174-.1897 5.5289-.1943 1.4447-.5996 2.5848-1.4944 3.4796-.8284.8284-1.8678 1.2379-3.1665 1.4481-1.2768.2067-2.8829.2318-4.898.2354-.4142.0008-.7506-.3344-.7514-.7486-.0007-.4142.3344-.7506.7487-.7514 2.0399-.0037 3.523-.0319 4.661-.2161 1.116-.1807 1.8169-.4995 2.3455-1.0281.5697-.5697.8966-1.3404 1.0684-2.6188.1748-1.3.1764-3.0082.1764-5.3864 0-2.37822-.0016-4.08644-.1764-5.38642-.1718-1.27841-.4987-2.04913-1.0684-2.61878-.5696-.56966-1.3404-.89655-2.6188-1.06843-1.2999-.17478-3.0082-.17637-5.3864-.17637-2.37821 0-4.08643.00159-5.38641.17637-1.2784.17188-2.04912.49877-2.61878 1.06843-.52862.52861-.84741 1.22947-1.02806 2.34551-.18419 1.13796-.21249 2.62112-.21616 4.66109-.00075.4142-.33714.7493-.75135.7486s-.74939-.3371-.74865-.7514c.00363-2.0151.02877-3.62119.23543-4.89797.21022-1.2987.61971-2.33808 1.44813-3.16649.89481-.89481 2.03483-1.30015 3.47957-1.49439 1.41149-.18977 3.22052-.18976 5.52888-.18975z" />
                          <path d="m12.5 7c0-.41421.3358-.75.75-.75h3.75c.4142 0 .75.33579.75.75v3.75c0 .4142-.3358.75-.75.75s-.75-.3358-.75-.75v-1.93934l-3.7197 3.71964c-.2929.2929-.7677.2929-1.0606 0s-.2929-.7677 0-1.0606l3.7196-3.7197h-1.9393c-.4142 0-.75-.33579-.75-.75z" />
                          <path
                            clipRule="evenodd"
                            d="m5.948 13.25c-.89848 0-1.6477-.0001-2.24248.0799-.62779.0844-1.19459.2701-1.65006.7256-.45547.4554-.64114 1.0222-.72555 1.65-.07996.5948-.07994 1.344-.07991 2.2425v.104c-.00003.8985-.00005 1.6477.07991 2.2425.08441.6278.27008 1.1946.72555 1.65.45547.4555 1.02227.6412 1.65006.7256.59478.08 1.34399.0799 2.24246.0799h.10402c.89847 0 1.64771.0001 2.24248-.0799.6278-.0844 1.1946-.2701 1.65007-.7256.45545-.4554.64115-1.0222.72555-1.65.08-.5948.0799-1.344.0799-2.2425v-.104c0-.8985.0001-1.6477-.0799-2.2425-.0844-.6278-.2701-1.1946-.72555-1.65-.45547-.4555-1.02227-.6412-1.65007-.7256-.59478-.08-1.344-.0799-2.24248-.0799zm-2.83188 1.8661c.13032-.1303.32781-.2375.78928-.2996.48303-.0649 1.13059-.0665 2.0946-.0665s1.61157.0016 2.09461.0665c.46146.0621.65896.1693.78928.2996.13031.1303.23754.3278.29958.7893.06494.483.06653 1.1306.06653 2.0946s-.00159 1.6116-.06653 2.0946c-.06204.4615-.16927.659-.29958.7893-.13032.1303-.32782.2375-.78928.2996-.48304.0649-1.1306.0665-2.09461.0665s-1.61157-.0016-2.0946-.0665c-.46147-.0621-.65896-.1693-.78928-.2996s-.23754-.3278-.29958-.7893c-.06494-.483-.06654-1.1306-.06654-2.0946s.0016-1.6116.06654-2.0946c.06204-.4615.16926-.659.29958-.7893z"
                            fillRule="evenodd"
                          />
                        </g>
                      </svg>
                      <FormattedMessage id="see_details" />
                    </a>
                  </p>
                </div>
              </div>
              <div className="row">
                {/* {JSON.stringify(apponitmentDetail)} */}
                <div className="col-6">
                  <label className="text-muted bold">
                    {formatMessage("appointment_status")}
                  </label>{" "}
                  {/* Appointment status label */}
                </div>
                <div className="col-6">
                  <p className="mw-100">
                    <span
                      className={`badge ${
                        apponitmentDetail.AppointmentStatus == 0
                          ? "bg-success"
                          : apponitmentDetail.AppointmentStatus == 1
                          ? "bg-danger"
                          : apponitmentDetail.AppointmentStatus == 2
                          ? "bg-success"
                          : "bg-warning"
                      }`}
                    >
                      {formatMessage(bookingStatusEnum[apponitmentDetail?.AppointmentStatus])}
                    </span>
                    {/* Appointment status */}
                  </p>
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <label className="text-muted bold">
                    {formatMessage("staff_preference")}
                  </label>{" "}
                  {/* Staff name label */}
                </div>
                <div className="col-6">
                  <p className="mw-100">{apponitmentDetail?.StaffPreference == 1 ? formatMessage("male_hope") : apponitmentDetail.StaffPreference == 2 ? formatMessage("women_hope") : apponitmentDetail?.StaffPreference == 0 ? formatMessage("no_nomination") : formatMessage("specific")}</p>{" "}
                  {/* Staff name */}
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <label className="text-muted bold">
                    {formatMessage("staff_name")}
                  </label>{" "}
                  {/* Staff name label */}
                </div>
                <div className="col-6">
                  <p className="mw-100">{apponitmentDetail?.StaffName}</p>{" "}
                  {/* Staff name */}
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <label className="text-muted bold">
                    {formatMessage("date")}{" "}
                  </label>{" "}
                  {/* Date label */}
                </div>
                <div className="col-6">
                  <p className="mw-100">
                    {dayjs(props.event?.Date).format(DATE_FORMAT)}{" "}
                    {/* Formatted date */}
                  </p>
                </div>
              </div>
            </div>
            <h4 className="mt-3 treatment">
              <span className="treatmenttext ms-1">
                {formatMessage("treatment")}
              </span>{" "}
              {/* Treatment section */}
            </h4>
            <div className="bg-light p-3 rounded">
              {treatments?.map((treatment: any) => {
                let serviceName =
                  treatment.ServiceType === serviceTypeEnum.Option
                    ? getOptionNameFromServiceName(treatment?.ServiceName)
                    : treatment?.ServiceName;
                return (
                  <React.Fragment>
                    <div className="row">
                      <div className="col-6">
                        <label className="text-muted bold text-wrap">
                          {serviceName} 
                          {
                            treatment.ServiceType === serviceTypeEnum.Option &&
                            <span className="badge bg-primary rounded ms-2">
                              <FormattedMessage id="option" />
                            </span>
                          }
                        </label>
                      </div>
                      <div className="col-6">
                        <p className="mw-100">
                          {treatment?.StartTime} ~ {treatment?.EndTime}{" "}
                          {/* Treatment timings */}
                        </p>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            <div className="row">
              <ReservationMemoForm
                id={apponitmentDetail?.id} // Appointment ID
                initialValues={initialValues} // Initial form values
                isLoading={
                  updateReservationMemoState.inProgress || // Memo update in progress
                  updateReservationCancelState.inProgress || // Cancel update in progress
                  appointmentByIdState.inProgress // Appointment fetch in progress
                }
                staffId={apponitmentDetail?.AssignedStaffId} // Assigned staff ID
                submitHandler={onSubmitForm} // Form submit handler
                cancelReservationSubmitHandler={cancelReservationSubmitHandler} // Cancel reservation submit handler
                setSelectedAppointmentIdForPayment={
                  setSelectedAppointmentIdForPayment
                } // Set selected appointment ID for payment
                storeId={props.storeId} // Store ID
                viewOnly={props.viewOnly} // View-only flag
              />
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default ShowPopupHOC(
  CancelReservationPopupHOC(
    RebookReservationPopupHOC(observer(ReservationDetailsComponent))
  )
); // Exporting component with HOC and observer
